import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Award, Check, Loader2, AlertCircle, BarChart3, ChevronDown, ChevronUp, Gift, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Card de assinatura mensal — usado tanto pela clínica quanto pelo
 * profissional para pedir o selo Ouro/Prata/Bronze.
 *
 * Props:
 *   kind      "prof" | "clinic"        Muda preços e endpoint
 *   entity    doc com certification_*  Detecta plano atual e vitalício
 */

// Preços refletem PROF_PRICES / CLINIC_PRICES em backend/routers/subscriptions.py
const PRICE_BY_KIND = {
  prof:   { bronze: 50,  prata: 100, ouro: 150 },
  clinic: { bronze: 100, prata: 200, ouro: 300 },
};

const ENDPOINT_BY_KIND = {
  prof:   "/subscriptions/professional/checkout",
  clinic: "/subscriptions/clinic/checkout",
};

const LABEL_BY_KIND = {
  prof:   "seu perfil profissional",
  clinic: "sua unidade",
};

const BASE_PLANS = [
  {
    tier: "bronze", label: "Bronze",
    color: "bg-orange-50 border-orange-300 text-orange-900",
    badge: "bg-orange-500",
    features: [
      "Selo Bronze na vitrine pública",
      "Perfil listado",
      "Renovação automática mensal",
    ],
  },
  {
    tier: "prata", label: "Prata",
    color: "bg-slate-50 border-slate-400 text-slate-900",
    badge: "bg-slate-500",
    features: [
      "Selo Prata na vitrine pública",
      "Destaque médio nos matches",
      "Cancele quando quiser",
    ],
  },
  {
    tier: "ouro", label: "Ouro",
    color: "bg-amber-50 border-amber-400 text-amber-900",
    badge: "bg-amber-500", highlight: true,
    features: [
      "Selo Ouro (topo da vitrine)",
      "Máxima prioridade nos matches",
      "Destaque em campanhas RAF",
    ],
  },
];

const isCertActive = (entity) => {
  if (!entity?.certification_tier) return false;
  if (!["paid", "waived", "trial"].includes(entity.certification_payment_status)) return false;
  if (!entity.certification_expires_at) return false;
  return new Date(entity.certification_expires_at) > new Date();
};

const SubscriptionPlansCard = ({ kind = "clinic", entity }) => {
  const [busy, setBusy] = useState(null);
  const [stripeUnavailable, setStripeUnavailable] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [tierStats, setTierStats] = useState(null);
  const prices = PRICE_BY_KIND[kind];
  const endpoint = ENDPOINT_BY_KIND[kind];
  const scopeLabel = LABEL_BY_KIND[kind];
  const active = isCertActive(entity);
  const currentTier = active ? entity?.certification_tier : null;
  const isLifetime = !!entity?.certification_is_lifetime;
  // Cortesia = admin concedeu sem cobrança (waived). Pode ser vitalícia OU
  // com prazo. Em ambos os casos bloqueamos assinatura pra evitar cobrança
  // desnecessária — se admin revogar, o campo muda e plans destravam sozinhos.
  const isCourtesy = active && entity?.certification_payment_status === "waived";
  // Trava assinatura quando há qualquer selo cortesia ou vitalício ativo.
  const locked = isLifetime || isCourtesy;
  const expiresLabel = entity?.certification_expires_at
    ? new Date(entity.certification_expires_at).toLocaleDateString("pt-BR")
    : null;

  useEffect(() => {
    if (!showCompare || tierStats) return;
    api.get("/public/tier-stats")
      .then((r) => setTierStats(r.data?.[kind] || {}))
      .catch(() => setTierStats({}));
  }, [showCompare, tierStats, kind]);

  const subscribe = async (tier) => {
    setBusy(tier);
    setStripeUnavailable(false);
    try {
      const r = await api.post(endpoint, { tier, origin_url: window.location.origin });
      if (r?.data?.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error("Não foi possível iniciar o checkout");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 501) setStripeUnavailable(true);
      else toast.error(e?.response?.data?.detail || "Erro ao assinar");
    } finally { setBusy(null); }
  };

  const subscribeOneshot = async (tier) => {
    setBusy(`oneshot-${tier}`);
    setStripeUnavailable(false);
    try {
      const url = kind === "prof" ? "/subscriptions/oneshot/professional" : "/subscriptions/oneshot/clinic";
      const r = await api.post(url, { tier, origin_url: window.location.origin });
      if (r?.data?.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error("Não foi possível iniciar o pagamento avulso");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 501) setStripeUnavailable(true);
      else toast.error(e?.response?.data?.detail || "Erro ao pagar avulso");
    } finally { setBusy(null); }
  };

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5"
         data-testid={`${kind}-subscription-plans`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display font-bold text-lg text-[#0B2B62] flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" /> Planos de credenciamento mensal
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Escolha um selo e destaque {scopeLabel} na vitrine pública. Cobrança recorrente via Stripe (cartão ou PIX) — cancele quando quiser.
          </p>
        </div>
        {currentTier && (
          <div className="text-right text-xs">
            <div className="text-slate-500">Plano atual</div>
            <div className="font-mono uppercase tracking-widest font-bold text-[#0B2B62] flex items-center gap-1.5 justify-end">
              <Award className="w-3.5 h-3.5" /> {currentTier}
              {isLifetime && <span className="text-[9px] bg-amber-100 border border-amber-300 text-amber-800 px-1 rounded uppercase font-bold">★ vitalício</span>}
              {isCourtesy && !isLifetime && <span className="text-[9px] bg-emerald-100 border border-emerald-300 text-emerald-800 px-1 rounded uppercase font-bold">cortesia</span>}
            </div>
          </div>
        )}
      </div>

      {isLifetime && (
        <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2"
             data-testid="lifetime-note">
          <Award className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Selo {currentTier?.toUpperCase()} cortesia vitalícia</b> concedido pela RAF Direct. Você não precisa assinar plano mensal enquanto ele estiver ativo.
            <div className="mt-1 text-amber-800">Este selo é uma cortesia e pode ser revogado a qualquer momento pelo admin. Se isso acontecer, esta seção libera automaticamente para assinatura.</div>
          </div>
        </div>
      )}

      {isCourtesy && !isLifetime && (
        <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2"
             data-testid="courtesy-note">
          <Award className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Selo {currentTier?.toUpperCase()} cortesia RAF Direct{expiresLabel ? ` (válido até ${expiresLabel})` : ""}</b> — assinatura desnecessária enquanto ativo.
            <div className="mt-1 text-emerald-800">Este selo é uma cortesia e pode ser revogado a qualquer momento pelo admin. Se isso acontecer, esta seção libera automaticamente para assinatura.</div>
          </div>
        </div>
      )}

      {entity?.certification_is_trial && active && !locked && (
        <div className="mb-4 p-3 rounded-md bg-sky-50 border border-sky-200 text-xs text-sky-900 flex items-start gap-2"
             data-testid="trial-note">
          <Award className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Você está em período de teste grátis</b> — seu selo <b>{currentTier?.toUpperCase()}</b> vale até <b>{new Date(entity.certification_expires_at).toLocaleDateString("pt-BR")}</b>. Depois disso, escolha um plano abaixo para manter seu selo ativo e continuar aparecendo na vitrine.
          </div>
        </div>
      )}

      {stripeUnavailable && (
        <div className="mb-4 p-3 rounded-md bg-slate-50 border border-slate-300 text-xs text-slate-700 flex items-start gap-2"
             data-testid="stripe-unavailable-note">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-500" />
          <div><b>Assinaturas ainda não disponíveis.</b> A RAF Direct está finalizando a configuração do processador de pagamentos. Tente novamente em breve ou fale com o consultor RAF pelo WhatsApp para credenciamento manual.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {BASE_PLANS.map((p) => {
          const price = prices[p.tier];
          const isCurrent = currentTier === p.tier && active;
          return (
            <div key={p.tier}
                 data-testid={`plan-card-${p.tier}`}
                 className={`relative rounded-xl border-2 p-4 ${p.color} ${p.highlight ? "ring-2 ring-amber-300 ring-offset-1" : ""}`}>
              {p.highlight && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-black uppercase tracking-widest bg-amber-500 text-white px-2 py-0.5 rounded">
                  Mais escolhido
                </div>
              )}
              <div className={`inline-block text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-widest text-white ${p.badge}`}>
                {p.label}
              </div>
              <div className="mt-2 font-display">
                <span className="text-3xl font-bold">R$ {price}</span>
                <span className="text-xs text-slate-500 ml-1">/mês</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs">
                    <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(p.tier)}
                disabled={busy !== null || locked || isCurrent}
                data-testid={`plan-subscribe-${p.tier}`}
                className={`mt-4 w-full py-2 rounded-md text-xs font-bold uppercase tracking-widest transition-colors ${
                  isCurrent ? "bg-emerald-700 text-white cursor-default"
                  : locked ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : p.highlight ? "bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  : "bg-[#0B2B62] text-white hover:bg-[#08204a] disabled:opacity-50"
                }`}>
                {busy === p.tier ? (
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Redirecionando...</span>
                ) : isCurrent ? "Plano atual"
                  : isLifetime ? "Vitalício ativo"
                  : isCourtesy ? "Cortesia ativa"
                  : currentTier ? "Trocar para este" : "Assinar recorrente"}
              </button>
              {!locked && !isCurrent && (
                <button
                  onClick={() => subscribeOneshot(p.tier)}
                  disabled={busy !== null}
                  data-testid={`plan-oneshot-${p.tier}`}
                  title={`Pague 1 mês via PIX ou Cartão (R$ ${(price * 1.10).toFixed(2).replace(".", ",")}). Selo ativo por 30 dias, sem cobrança automática. Você renova quando quiser.`}
                  className="mt-2 w-full py-2 rounded-md text-[11px] font-bold uppercase tracking-widest transition-colors border-2 border-cyan-600 text-cyan-800 hover:bg-cyan-50 disabled:opacity-40">
                  {busy === `oneshot-${p.tier}` ? (
                    <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Redirecionando...</span>
                  ) : (
                    <>Pagar 1 mês · PIX/Cartão</>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[10px] text-slate-500">
        Pagamento seguro via Stripe · Cartão · PIX · Nota fiscal automática · Cancele quando quiser no próprio painel Stripe (link enviado por e-mail).
      </p>

      {/* Combo Pague 3 Ganhe 1 (one-time, aceita PIX) */}
      <ComboBanner kind={kind} entity={entity} prices={prices} isLifetime={locked} />

      {/* Comparador de planos com métricas históricas */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <button onClick={() => setShowCompare(!showCompare)}
                data-testid="toggle-compare-plans"
                className="w-full flex items-center justify-between text-xs font-semibold text-[#0B2B62] hover:text-[#08204a] transition-colors">
          <span className="inline-flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            Comparar planos em detalhe {tierStats ? `(dados dos últimos 30 dias)` : ""}
          </span>
          {showCompare ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showCompare && (
          <div className="mt-4 overflow-x-auto" data-testid="compare-plans-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-3 font-semibold text-slate-500 uppercase tracking-widest text-[10px]">Item</th>
                  <th className="text-center py-2 px-3 font-black text-orange-600 uppercase text-[10px]">Bronze</th>
                  <th className="text-center py-2 px-3 font-black text-slate-600 uppercase text-[10px]">Prata</th>
                  <th className="text-center py-2 px-3 font-black text-amber-700 uppercase text-[10px]">Ouro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 pr-3 font-semibold text-slate-700">Preço mensal</td>
                  <td className="py-2 px-3 text-center font-mono">R$ {prices.bronze}</td>
                  <td className="py-2 px-3 text-center font-mono">R$ {prices.prata}</td>
                  <td className="py-2 px-3 text-center font-mono font-bold text-amber-700">R$ {prices.ouro}</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 font-semibold text-slate-700">Posição na vitrine</td>
                  <td className="py-2 px-3 text-center text-slate-500">Padrão</td>
                  <td className="py-2 px-3 text-center text-slate-600">Destaque médio</td>
                  <td className="py-2 px-3 text-center text-amber-700 font-bold">Topo</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 font-semibold text-slate-700">Prioridade em matches</td>
                  <td className="py-2 px-3 text-center">1x</td>
                  <td className="py-2 px-3 text-center">2x</td>
                  <td className="py-2 px-3 text-center font-bold">3x</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 font-semibold text-slate-700">Campanhas RAF</td>
                  <td className="py-2 px-3 text-center text-slate-300">—</td>
                  <td className="py-2 px-3 text-center text-slate-300">—</td>
                  <td className="py-2 px-3 text-center text-emerald-700"><Check className="w-3.5 h-3.5 inline" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 font-semibold text-slate-700">Suporte dedicado</td>
                  <td className="py-2 px-3 text-center text-slate-300">—</td>
                  <td className="py-2 px-3 text-center text-slate-300">—</td>
                  <td className="py-2 px-3 text-center text-emerald-700"><Check className="w-3.5 h-3.5 inline" /></td>
                </tr>
                {tierStats && (
                  <>
                    <tr className="bg-slate-50">
                      <td className="py-2 pr-3 font-semibold text-slate-700 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Média de matches (30d)</td>
                      <td className="py-2 px-3 text-center font-mono">{tierStats.bronze?.avg_matches_30d ?? "—"}</td>
                      <td className="py-2 px-3 text-center font-mono">{tierStats.prata?.avg_matches_30d ?? "—"}</td>
                      <td className="py-2 px-3 text-center font-mono font-bold">{tierStats.ouro?.avg_matches_30d ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-3 font-semibold text-slate-500 text-[10px]">Ativos hoje na rede</td>
                      <td className="py-2 px-3 text-center text-slate-500">{tierStats.bronze?.count ?? "—"}</td>
                      <td className="py-2 px-3 text-center text-slate-500">{tierStats.prata?.count ?? "—"}</td>
                      <td className="py-2 px-3 text-center text-slate-500">{tierStats.ouro?.count ?? "—"}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            <p className="mt-2 text-[10px] text-slate-400 italic">
              Métricas atualizadas em tempo real com base nos casos direcionados pela RAF Direct nos últimos 30 dias. Números individuais podem variar conforme localização, categoria e disponibilidade.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlansCard;

/**
 * ComboBanner — Oferta "Pague 3, ganhe 1 mês grátis" com pagamento one-time
 * (aceita PIX!). Ideal pra clientes que preferem PIX à recorrência de cartão.
 */
const ComboBanner = ({ kind, entity, prices, isLifetime }) => {
  const [comboTier, setComboTier] = useState("ouro");
  const [busy, setBusy] = useState(false);
  const monthly = prices[comboTier];
  const total = monthly * 3;
  const endpoint = kind === "prof" ? "/subscriptions/combo/professional" : "/subscriptions/combo/clinic";

  if (isLifetime) return null;

  const contract = async () => {
    setBusy(true);
    try {
      const r = await api.post(endpoint, { tier: comboTier, origin_url: window.location.origin });
      if (r?.data?.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error("Não foi possível iniciar o checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao contratar o combo");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-5 relative overflow-hidden"
         data-testid="combo-pague3ganhe1-banner">
      <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-bl-lg">
        <Sparkles className="w-3 h-3 inline mr-1" /> Oferta PIX
      </div>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-display font-bold text-[#0B2B62] text-base">Pague 3 meses, ganhe 1 grátis</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Pagamento à vista via <b>PIX ou cartão</b> — sem recorrência automática. Ao pagar,
            o selo <b>{comboTier.toUpperCase()}</b> vale <b>4 meses cheios</b> e some sozinho depois (você pode renovar).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {["bronze", "prata", "ouro"].map((t) => (
          <button key={t} onClick={() => setComboTier(t)}
                  data-testid={`combo-tier-${t}`}
                  className={`text-left p-2.5 rounded-lg border-2 transition-all ${
                    comboTier === t ? "border-emerald-500 bg-white shadow-md"
                                    : "border-slate-200 bg-white/60 hover:border-slate-300"
                  }`}>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{t}</div>
            <div className="mt-1 font-display">
              <div className="text-lg font-bold text-emerald-700">R$ {prices[t] * 3}</div>
              <div className="text-[10px] text-slate-500 -mt-0.5">à vista · 4 meses de selo</div>
            </div>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-emerald-200">
        <div className="text-xs text-slate-700">
          <div>
            <span className="line-through text-slate-400">R$ {monthly * 4}</span>{" "}
            <b className="text-emerald-700">R$ {total}</b>
            <span className="ml-2 inline-block bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded">
              −25% · 1 mês grátis
            </span>
          </div>
          <div className="text-[11px] text-emerald-700 mt-0.5">
            💰 Economia de <b>R$ {monthly}</b> vs 4 meses no cartão · Pague via PIX à vista
          </div>
        </div>
        <button onClick={contract} disabled={busy}
                data-testid="combo-contract-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-sm font-bold rounded-md transition-colors">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecionando…</> :
                  <><Gift className="w-4 h-4" /> Pagar R$ {total} via PIX/Cartão</>}
        </button>
      </div>
    </div>
  );
};
